// Minimal Wacom STU signature-pad driver over WebHID (Chrome/Edge, HTTPS).
// Protocol notes adapted from pabloko/Wacom-STU-WebHID (MIT) and eros23/Wacom-STU-430-WebHID.
const WACOM_VID = 0x056a;
// productId -> [tabletW, tabletH, screenW, screenH, colour]
const MODELS = { 164: [9600, 6000, 320, 200, false], 166: [9600, 6000, 320, 200, false], 165: [10800, 6480, 800, 480, true], 168: [10800, 6480, 800, 480, true] };
const R = { penData: 0x01, capability: 0x09, clearScreen: 0x20, inkMode: 0x21, imgStart: 0x25, imgData: 0x26, imgEnd: 0x27, penDataTiming: 0x34 };
const CHUNK = 253;

export const webHidSupported = () => typeof navigator !== "undefined" && !!navigator.hid && window.isSecureContext;

export class WacomStu {
  constructor() { this.device = null; this.size = [10800, 6480]; this.screen = { w: 800, h: 480, colour: true }; this.onPen = null; this._handler = null; }

  get connected() { return !!this.device?.opened; }
  get name() { return this.device?.productName || "Wacom STU"; }

  async reconnect() {
    if (!webHidSupported()) return false;
    const [dev] = (await navigator.hid.getDevices()).filter((d) => d.vendorId === WACOM_VID);
    return dev ? this._open(dev) : false;
  }

  async connect() {
    if (!webHidSupported()) return false;
    const [dev] = await navigator.hid.requestDevice({ filters: [{ vendorId: WACOM_VID }] });
    return dev ? this._open(dev) : false;
  }

  async _open(dev) {
    if (!dev.opened) await dev.open();
    this.device = dev;
    const m = MODELS[dev.productId];
    if (m) { this.size = [m[0], m[1]]; this.screen = { w: m[2], h: m[3], colour: m[4] }; }
    try {
      const dv = await dev.receiveFeatureReport(R.capability);
      const w = dv.getUint16(1), h = dv.getUint16(3), sw = dv.getUint16(7), sh = dv.getUint16(9);
      if (w > 1000 && h > 1000) this.size = [w, h];
      if (sw >= 300 && sw <= 1280 && sh >= 200 && sh <= 1024) this.screen = { ...this.screen, w: sw, h: sh };
    } catch { /* some models (STU-430) don't expose capability */ }
    this._handler = (e) => {
      if (!this.onPen || (e.reportId !== R.penData && e.reportId !== R.penDataTiming)) return;
      const d = e.data;
      const b0 = d.getUint8(0);
      const press = (((b0 & 0x0f) << 8) | d.getUint8(1));
      this.onPen({ down: (b0 & 0x02) !== 0 || press > 0, x: d.getUint16(2) / this.size[0], y: d.getUint16(4) / this.size[1], press });
    };
    dev.addEventListener("inputreport", this._handler);
    await this.clear();
    await this._send(R.inkMode, [1]);
    return true;
  }

  async clear() { await this._send(R.clearScreen, [0]); }

  // Draw `paint(ctx, w, h)` at the pad's native resolution and push it to the pad screen.
  async showImage(paint) {
    if (!this.connected) return;
    const { w, h, colour } = this.screen;
    const cv = Object.assign(document.createElement("canvas"), { width: w, height: h });
    const g = cv.getContext("2d");
    paint(g, w, h, colour);
    const px = g.getImageData(0, 0, w, h).data;
    let bytes, mode;
    if (colour) {
      mode = 0x04;
      bytes = new Uint8Array(w * h * 3);
      for (let i = 0, j = 0; i < px.length; i += 4, j += 3) { bytes[j] = px[i + 2]; bytes[j + 1] = px[i + 1]; bytes[j + 2] = px[i]; }
    } else {
      mode = 0x00;
      const stride = Math.ceil(w / 8);
      bytes = new Uint8Array(stride * h);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114 < 140) bytes[y * stride + (x >> 3)] |= 0x80 >> (x & 7);
      }
    }
    await this._send(R.imgStart, [mode]);
    for (let o = 0; o < bytes.length; o += CHUNK) {
      const part = bytes.subarray(o, o + CHUNK);
      const pkt = new Uint8Array(CHUNK + 2);
      pkt[0] = part.length & 0xff; pkt[1] = part.length >> 8;
      pkt.set(part, 2);
      await this._send(R.imgData, pkt);
    }
    await this._send(R.imgEnd, [0]);
  }

  async _send(id, bytes) {
    try { await this.device?.sendFeatureReport(id, bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)); } catch { /* pad busy or unsupported */ }
  }

  async close() {
    if (!this.device) return;
    this.device.removeEventListener("inputreport", this._handler);
    await this.clear();
    try { await this.device.close(); } catch { /* already closed */ }
    this.device = null;
  }
}
